import { Suspense, useMemo } from 'react';
import { Loader2 } from 'lucide-react';
import { Alert, AlertDescription, AlertTitle } from '@reflex/ui/components/ui/alert';
import { PageDescription } from '@reflex/ui/components/layout/Typography';
import { getPluginComponent } from '@/lib/plugin-registry';
import type { PluginManifest } from '@/lib/plugin-registry';

/**
 * Resolves a plugin-contributed admin setup component from the plugin
 * registry and renders it inside a `<Suspense>` boundary. Used by the
 * per-plugin admin setup route (`/admin/plugins/:name/setup`) and by the
 * first-run setup wizard's Integrations stage — the panel is the same
 * component in both surfaces so plugins implement it once.
 *
 * Returns `null` when the plugin does not declare `adminSetupComponent`
 * or the referenced component is not registered (silently skipped, with
 * the wiring gap surfaced separately via `collectPluginWebWiringIssues`).
 */
export function PluginAdminSetupPanel({ plugin }: { plugin: PluginManifest }) {
  const adminSetup = plugin.web?.adminSetupComponent;
  // Resolve the component once per render. `getPluginComponent` reads a
  // stable module-level component keyed by `componentPath`
  // (built once at plugin-registry boot via `import.meta.glob`), so the
  // returned reference is identity-stable across renders — there is no
  // state-reset risk that the `react-hooks/static-components` rule warns
  // about. The same pattern is used by other dynamic plugin slots; see
  // `App.tsx` where each plugin route does
  // `const { component: Component } = entry`.
  const resolved = useMemo(
    () => ({ component: adminSetup ? getPluginComponent(adminSetup.componentPath) : undefined }),
    [adminSetup],
  );
  const { component: Component } = resolved;

  if (!adminSetup) return null;

  if (!Component) {
    return (
      <Alert variant="destructive" data-testid="plugin-admin-setup-panel-missing">
        <AlertTitle>Admin setup panel not registered</AlertTitle>
        <AlertDescription>
          The <code className="font-mono">{plugin.name}</code> plugin declared an admin setup
          component (<code className="font-mono">{adminSetup.componentPath}</code>) but it is not
          registered in its <code className="font-mono">web/register.ts</code>.
        </AlertDescription>
      </Alert>
    );
  }

  return (
    <section
      data-testid={`plugin-admin-setup-panel-${plugin.name}`}
      className="flex flex-col gap-3"
    >
      {adminSetup.label && (
        <PageDescription className="text-xs">{adminSetup.label}</PageDescription>
      )}
      <Suspense
        fallback={
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            <Loader2 className="h-3 w-3 animate-spin" />
            Loading setup panel…
          </div>
        }
      >
        <Component />
      </Suspense>
    </section>
  );
}
